// #PRINT_VIEW_V1 (2026-10-23) — Bill: "items to print queue in downloads vs viewing and then choosing to print" and
// "on mobile you open a PO, no close button, so you have to close the app and reopen."
// One on-screen viewer panel on top of DOPE for printable pages and PDFs. It never opens a new browser window (a new
// window inside the home-screen app has no close / back button) and never drops a file into Downloads on its own.
// The bar always has: 🖨 Print · ⬇ Save (or Share on a phone) · ✕ Close (Esc). Close sits clear of the phone notch.
//   const pv = openPrintView({ title, status });   // shows right away (call it straight from the click)
//   pv.setHtml(html, title)   — a printable HTML page (PO sheet, packet)
//   pv.setPdf(blob, filename) — a finished PDF (jsPDF output('blob'))
//   pv.setStatus(text) / pv.fail(err) / pv.close()
const PV_ID = 'dope-print-view';
const isPhone = () => { try { return window.matchMedia('(pointer:coarse)').matches || window.innerWidth < 760; } catch { return false; } };

let _pdfjs = null;
const loadPdfJs = () => _pdfjs || (_pdfjs = new Promise((resolve, reject) => {   // same app-hosted pdf.js the PO packet uses
  if (window.pdfjsLib) { resolve(window.pdfjsLib); return; }
  const sc = document.createElement('script'); sc.src = '/pdfjs/pdf.min.js';
  sc.onload = () => { try { window.pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdfjs/pdf.worker.min.js'; resolve(window.pdfjsLib); } catch (e) { _pdfjs = null; reject(e); } };
  sc.onerror = () => { _pdfjs = null; reject(new Error('the PDF reader did not load')); };
  document.head.appendChild(sc);
}));

export function openPrintView({ title = 'Print', status = 'Building…' } = {}) {
  const old = document.getElementById(PV_ID); if (old) old.remove();
  const phone = isPhone();
  const el = document.createElement('div'); el.id = PV_ID;
  el.style.cssText = 'position:fixed;inset:0;z-index:100001;background:rgba(0,0,0,.62);display:flex;align-items:center;justify-content:center;font-family:inherit';
  const box = document.createElement('div');
  box.style.cssText = phone
    ? 'position:fixed;inset:0;background:#1B1D21;display:flex;flex-direction:column;overflow:hidden'
    : 'width:min(1100px,94vw);height:92vh;background:#1B1D21;border:1px solid #3A3D42;border-radius:10px;display:flex;flex-direction:column;overflow:hidden;box-shadow:0 18px 50px #000b';
  const bar = document.createElement('div');
  bar.style.cssText = 'display:flex;align-items:center;gap:8px;padding:8px 12px;border-bottom:1px solid #3A3D42;color:#EDEEF0;font-size:13px;font-weight:700;flex:0 0 auto'
    + (phone ? ';padding-top:calc(8px + env(safe-area-inset-top, 0px))' : '');
  const ttl = document.createElement('div'); ttl.textContent = '🖨 ' + title;
  ttl.style.cssText = 'flex:1 1 auto;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap';
  const mkBtn = (label, tip, gold) => { const b = document.createElement('button'); b.type = 'button'; b.textContent = label; b.title = tip;
    b.style.cssText = `background:${gold ? '#FFB020' : 'transparent'};color:${gold ? '#1a1206' : '#EDEEF0'};border:1px solid ${gold ? '#FFB020' : '#3A3D42'};border-radius:6px;padding:${phone ? '7px 11px' : '4px 10px'};font-size:12px;font-weight:800;cursor:pointer;white-space:nowrap`;
    b.disabled = true; b.style.opacity = '.45'; return b; };
  const pr = mkBtn('🖨 Print', 'Pick the printer (or Save as PDF) in the print window', true);
  const sv = mkBtn(phone ? '⇪ Share' : '⬇ Save', phone ? 'Share / save / print from the phone' : 'Save a copy to Downloads');
  const x = document.createElement('button'); x.type = 'button'; x.textContent = phone ? '✕ Close' : '✕ Close (Esc)';
  x.style.cssText = `background:#2A2C30;border:1px solid #5A5D62;color:#fff;border-radius:6px;padding:${phone ? '7px 12px' : '4px 10px'};font-size:12px;font-weight:800;cursor:pointer;white-space:nowrap`;
  bar.append(ttl, pr, sv, x);
  const body = document.createElement('div');
  body.style.cssText = 'flex:1 1 auto;min-height:0;background:#2A2C30;overflow:auto;-webkit-overflow-scrolling:touch;display:flex;flex-direction:column;align-items:center;color:#9AA0A8;font-size:13px';
  const say = (t) => { body.innerHTML = ''; const d = document.createElement('div'); d.style.cssText = 'margin:auto;padding:24px;text-align:center'; d.textContent = t; body.append(d); };
  say(status);
  box.append(bar, body); el.append(box); document.body.append(el);

  let url = null; let file = null; let frame = null; let docTitle = title;
  const close = () => { try { el.remove(); } catch {} document.removeEventListener('keydown', onKey, true); if (url) setTimeout(() => URL.revokeObjectURL(url), 1000); };
  const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); close(); } };
  document.addEventListener('keydown', onKey, true);
  x.onclick = close; el.onclick = (e) => { if (e.target === el) close(); };
  const on = (b) => { b.disabled = false; b.style.opacity = '1'; };
  // Print the frame; the browser names a "Save as PDF" after the top page title, so borrow it for the print.
  const printFrame = () => { if (!frame || !frame.contentWindow) return; const keep = document.title; document.title = docTitle;
    try { frame.contentWindow.focus(); frame.contentWindow.print(); } catch (e) { alert('Print failed: ' + ((e && e.message) || e)); }
    setTimeout(() => { document.title = keep; }, 1500); };
  const saveFile = async () => {
    if (!file) return;
    if (phone && navigator.canShare && navigator.canShare({ files: [file] })) { try { await navigator.share({ files: [file], title: docTitle }); } catch (e) { /* cancelled */ } return; }
    const a = document.createElement('a'); a.href = url || URL.createObjectURL(file); a.download = file.name; document.body.appendChild(a); a.click(); a.remove();
  };

  return {
    el,
    setStatus: say,
    fail: (e) => say('Could not build it: ' + ((e && e.message) || e)),
    close,
    setHtml(html, t) {
      if (!document.getElementById(PV_ID)) return;
      if (t) { docTitle = t; ttl.textContent = '🖨 ' + t; }
      // the page's own auto-print is dropped — Bill views first, then chooses Print
      const src = String(html).replace(/\sonload="setTimeout\(function\(\)\{window\.print\(\);\},\s*\d+\)"/i, '');
      body.innerHTML = ''; body.style.background = '#fff';
      frame = document.createElement('iframe'); frame.title = docTitle; frame.srcdoc = src;
      frame.style.cssText = 'width:100%;flex:1 1 auto;min-height:100%;border:0;background:#fff';
      body.append(frame);
      pr.onclick = printFrame; on(pr);
      sv.style.display = 'none';   // HTML sheets: Print → Save as PDF covers saving
    },
    async setPdf(blob, filename) {
      if (!document.getElementById(PV_ID)) return;
      docTitle = String(filename || title).replace(/\.pdf$/i, ''); ttl.textContent = '🖨 ' + docTitle;
      file = new File([blob], filename || 'report.pdf', { type: 'application/pdf' });
      url = URL.createObjectURL(file);
      sv.onclick = saveFile; on(sv);
      if (!phone) {   // #PDF_NAME_V1 — make ours the obvious Save, and say what it will be called
        sv.title = 'Save to Downloads as: ' + file.name;
        sv.style.background = '#FFB020'; sv.style.color = '#1a1206'; sv.style.borderColor = '#FFB020';
        pr.style.background = 'transparent'; pr.style.color = '#EDEEF0'; pr.style.borderColor = '#3A3D42';
      }
      if (!phone) {   // desktop: the browser's own PDF viewer, printed from here
        // #PDF_NAME_V1 (2026-10-29) — Bill: saved PDFs came down as random letters/numbers. That was Chrome's own
        // download icon inside its PDF toolbar: the file lives at a temporary blob: link, so Chrome invents a name.
        // Hide Chrome's toolbar here (#toolbar=0) so the only Save is ours, which always uses the real file name.
        body.innerHTML = ''; frame = document.createElement('iframe'); frame.title = docTitle; frame.src = url + '#toolbar=0&navpanes=0&view=FitH';
        frame.style.cssText = 'width:100%;flex:1 1 auto;border:0;background:#fff'; body.append(frame);
        pr.onclick = printFrame; on(pr); return;
      }
      // phone: phones only show page 1 of a PDF in a frame — draw every page instead; Print goes through Share
      say('Loading pages…');
      try {
        const lib = await loadPdfJs();
        const doc = await lib.getDocument({ data: await blob.arrayBuffer() }).promise;
        body.innerHTML = ''; body.style.padding = '8px 0';
        const w = Math.min(window.innerWidth - 16, 900);
        for (let p = 1; p <= doc.numPages; p++) {
          const page = await doc.getPage(p); const base = page.getViewport({ scale: 1 });
          const vp = page.getViewport({ scale: (w / base.width) * Math.min(2, window.devicePixelRatio || 1) });
          const c = document.createElement('canvas'); c.width = Math.round(vp.width); c.height = Math.round(vp.height);
          c.style.cssText = `width:${w}px;height:auto;background:#fff;margin:0 0 8px;box-shadow:0 2px 8px #0008`;
          await page.render({ canvasContext: c.getContext('2d'), viewport: vp }).promise;
          if (!document.getElementById(PV_ID)) return; body.append(c);
        }
      } catch (e) { say('Preview not available on this phone — use ⇪ Share to open or print it.'); }
      pr.onclick = saveFile; on(pr);   // Share sheet has Print on iPhone / Android
    },
  };
}

// For jsPDF builders: view the finished PDF instead of saving it straight to Downloads.
export const viewPdf = (pdf, filename, title) => { const pv = openPrintView({ title: title || String(filename || '').replace(/\.pdf$/i, '') }); pv.setPdf(pdf.output('blob'), filename); return pv; };
